import { React, useEffect, useState } from 'react';

// mui
import { TableContainer, Table, TableRow, TableBody, TableCell, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// client-shared
import { ContainerScroll } from '@goldblack/client-comps';
import { def } from '@goldblack/shared';

function fmtDate(val) {
    if (!val) return '';
    const d = typeof val === 'number' ? new Date(val) : new Date(Date.parse(val));
    return isNaN(d) ? String(val) : d.toLocaleString('ko-KR');
}

const svcCell = { borderBottom: 'none', py: '2px' };

function ServicePanel({ svc }) {
    return (
        <TableContainer>
            <Table size='small'>
                <TableBody>
                    <TableRow>
                        <TableCell sx={{ width: '30%', ...svcCell }}>code</TableCell>
                        <TableCell sx={{ width: '70%', ...svcCell }} align='right'>
                            {svc.code}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={svcCell}>userId</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all', ...svcCell }} align='right'>
                            {svc.userId ?? ''}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={svcCell}>type</TableCell>
                        <TableCell sx={svcCell} align='right'>
                            {svc.type != null ? (def.AccountTypeLabel[svc.type] ?? svc.type) : ''}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={svcCell}>status</TableCell>
                        <TableCell sx={svcCell} align='right'>
                            {svc.status != null ? (def.AccountStatusLabel[svc.status] ?? svc.status) : ''}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={svcCell}>joinedAt</TableCell>
                        <TableCell sx={svcCell} align='right'>
                            {fmtDate(svc.joinedAt)}
                        </TableCell>
                    </TableRow>
                    {svc.legal &&
                        Object.entries(svc.legal).map(([key, val]) => (
                            <TableRow key={key}>
                                <TableCell sx={svcCell}>legal.{key}</TableCell>
                                <TableCell sx={svcCell} align='right'>
                                    v{val.version} · {fmtDate(val.agreedAt)}
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function AccountUserInfoPanel({ user, config }) {
    const [createdAt, setCreatedAt] = useState('');
    const [updatedAt, setUpdatedAt] = useState('');
    const [loginAt, setLoginAt] = useState('');

    useEffect(() => {
        if (user) {
            setCreatedAt(fmtDate(user.createdAt));
            setUpdatedAt(fmtDate(user.updatedAt));
            setLoginAt(fmtDate(user.loginAt));
        } else {
            setCreatedAt('');
            setUpdatedAt('');
            setLoginAt('');
        }
    }, [user]);

    return (
        <ContainerScroll>
            <TableContainer>
                <Table sx={{ '& td, & th': { border: 1, borderColor: 'divider' } }}>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ width: '20%' }}>accountId</TableCell>
                            <TableCell sx={{ width: '80%', wordBreak: 'break-all' }} align='right'>
                                {user?._id}
                                <Tooltip title='copy to clipboard'>
                                    <IconButton
                                        size='small'
                                        sx={{ ml: 0.5 }}
                                        onClick={() => navigator.clipboard.writeText(user?._id ?? '')}>
                                        <ContentCopyIcon sx={{ fontSize: 14, color: 'text.primary' }} />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>email</TableCell>
                            <TableCell align='right'>{user?.email}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>username</TableCell>
                            <TableCell align='right'>{user?.username}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>image</TableCell>
                            <TableCell align='right'>
                                {(user?.thumbnail || user?.image) ? (
                                    <img
                                        src={user.thumbnail || user.image}
                                        alt=''
                                        width={120}
                                        height={120}
                                        style={{ borderRadius: '50%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    ''
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>type</TableCell>
                            <TableCell align='right'>
                                {user != null ? (def.AccountTypeLabel[user.type] ?? user.type) : ''}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>status</TableCell>
                            <TableCell align='right'>
                                {user != null ? (def.AccountStatusLabel[user.status] ?? user.status) : ''}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>birthDate</TableCell>
                            <TableCell align='right'>{user?.birthDate ?? ''}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>createdAt</TableCell>
                            <TableCell align='right'>{createdAt}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>updatedAt</TableCell>
                            <TableCell align='right'>{updatedAt}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>loginAt</TableCell>
                            <TableCell align='right'>{loginAt}</TableCell>
                        </TableRow>
                        {user?.services?.map((svc, i) => (
                            <TableRow key={i}>
                                <TableCell>{`service ${i}`}</TableCell>
                                <TableCell padding='none' align='right'>
                                    <ServicePanel svc={svc} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </ContainerScroll>
    );
}

export { AccountUserInfoPanel };
